// GCS signed URL 로 단일 파일을 PUT 한다. 한 번의 시도에 대한 전송만 담당한다.
//
// Owns:
// - XHR 사용 결정(load-bearing): fetch 는 업로드 진행 이벤트가 없어 파일 전송 중 바이트 단위
//   진행을 알 수 없다. XHR `upload.onprogress` 로 전송 중 진행을 전달한다.
// - abort 연결(AbortSignal → xhr.abort → AbortError reject)과 상태코드 분류.
// - 무진행 감시(`stallTimeoutMs`): 진행이 정해진 시간 동안 없으면 전송을 끊는다. 총 시간 한도
//   (`timeoutMs`, XHR `timeout`)와 다르다. 총 시간 한도는 느린 회선에서 큰 파일을 올리는 정상 전송도
//   끊는다. 그래서 파일 크기를 모르는 호출부는 무진행 감시를 쓴다.
// - 서버가 티켓에 명시한 헤더만 추가한다. 지정하지 않은 업로드에는 추가 헤더가 없다.
//
// Does not own:
// - 재시도/backoff/티켓 재발급. putPool 이 정책으로 소유한다.

// PUT 이 HTTP 응답으로 실패했을 때의 오류. putPool 이 status 로 재시도 여부를 분류한다.
export class PutFileHttpError extends Error {
  readonly status: number;

  constructor(status: number, statusText: string) {
    super(`Signed PUT failed: ${status} ${statusText}.`);
    this.name = "PutFileHttpError";
    this.status = status;
  }
}

// PUT 이 HTTP 응답 없이 끝났을 때의 오류. 연결 오류(onerror)와 무진행 중단이 여기에 든다. 서버가
// 거부한 것이 아니므로 putPool 이 응답 오류(PutFileHttpError)와 다르게 다룬다.
export class PutFileNetworkError extends Error {
  constructor(reason: string) {
    super(`Signed PUT failed: ${reason}.`);
    this.name = "PutFileNetworkError";
  }
}

export type PutFileOptions = {
  signal?: AbortSignal;
  headers?: Readonly<Record<string, string>>;
  // 전송의 총 시간 한도(ms). 넘으면 일반 Error 로 reject 한다.
  timeoutMs?: number;
  // 진행이 이 시간(ms) 동안 없으면 전송을 끊고 PutFileNetworkError 로 reject 한다. 타이머는 전송
  // 시작과 진행 이벤트마다 다시 건다. 값이 없으면 감시하지 않는다.
  stallTimeoutMs?: number;
  // 전송 중 누적 바이트(이 파일 기준 0부터). 완료되면 file.size 로 맞춘다.
  onProgress?: (loaded: number) => void;
};

export function putFileToSignedUrl(
  url: string,
  file: Blob,
  options: PutFileOptions = {},
): Promise<void> {
  const { signal, onProgress } = options;
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(new DOMException("Upload aborted", "AbortError"));
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = (): void => xhr.abort();
    // 무진행 타이머가 전송을 끊었는지. xhr.abort 는 사용자 중단과 같은 onabort 로 온다. 이 값으로 가른다.
    let stalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    const armStallTimer = (): void => {
      if (options.stallTimeoutMs === undefined) {
        return;
      }
      if (stallTimer !== null) {
        clearTimeout(stallTimer);
      }
      stallTimer = setTimeout(() => {
        stalled = true;
        xhr.abort();
      }, options.stallTimeoutMs);
    };
    const cleanup = (): void => {
      signal?.removeEventListener("abort", onAbort);
      if (stallTimer !== null) {
        clearTimeout(stallTimer);
      }
    };

    xhr.open("PUT", url);
    xhr.withCredentials = false;
    if (options.timeoutMs !== undefined) xhr.timeout = options.timeoutMs;
    for (const [name, value] of Object.entries(options.headers ?? {}))
      xhr.setRequestHeader(name, value);
    xhr.ontimeout = (): void => {
      cleanup();
      reject(new Error("Signed PUT timed out."));
    };
    xhr.upload.onprogress = (event): void => {
      armStallTimer();
      onProgress?.(event.loaded);
    };
    xhr.onload = (): void => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(file.size);
        resolve();
        return;
      }
      reject(new PutFileHttpError(xhr.status, xhr.statusText));
    };
    xhr.onerror = (): void => {
      cleanup();
      reject(new PutFileNetworkError("network error"));
    };
    xhr.onabort = (): void => {
      cleanup();
      reject(
        stalled
          ? new PutFileNetworkError("no upload progress")
          : new DOMException("Upload aborted", "AbortError"),
      );
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    xhr.send(file);
    armStallTimer();
  });
}
